/**
 * StrokeModifierPanel - "Cross-chair curve" (PARAMETRIC MODIFIER LANE, pass 2)
 *
 * Properties-panel section shown for the selected LINKED stroke. An enable
 * toggle plus a small inline 2D curve editor (shared CurveEditor widget):
 * t (cross-chair position, layer 1 -> layer N) on the horizontal axis, the
 * channel value on the vertical axis. Channels: X offset / Y offset
 * (sketch px ~ mm) and Width scale. 2-5 draggable control points,
 * Catmull-Rom interpolated (StrokeStore.evaluateCurve - the same
 * evaluator resolution uses).
 *
 * The modifier lives on the MASTER in the StrokeStore and is applied
 * during resolution, so linked layers fan out parametrically while
 * detached copies and all downstream consumers are untouched. Curve edits
 * write to the store and run the canonical regenerateAll; they do NOT
 * detach linked strokes (sync compares against the resolved geometry).
 */
import { defaultModifier, cloneModifier } from '../../core/StrokeStore.js';
import { CurveEditor } from '../CurveEditor.js';

const CHANNELS = [
    { key: 'offsetX', label: 'X', min: -150, max: 150, identity: 0, unit: 'mm' },
    { key: 'offsetY', label: 'Y', min: -150, max: 150, identity: 0, unit: 'mm' },
    { key: 'widthScale', label: 'Width', min: 0.25, max: 2.5, identity: 1, unit: 'x' }
];

export class StrokeModifierPanel {
    constructor(application) {
        this.app = application;
        this.channel = CHANNELS[0];
        this._lastSignature = null;
        this._lastRegen = 0;
        this.createPanel();
    }

    getElement() {
        return this.panel;
    }

    // --- Current selection plumbing --------------------------------------

    /** The selected stroke's id, or null when nothing usable is selected */
    _selectedStrokeId() {
        const sp = this.app.sketchPlane;
        const shape = sp?.selectedSpline || sp?.selectedPath;
        return shape?.strokeId || null;
    }

    /** Id if the selected stroke is LINKED (has a master), else null */
    _selectedLinkedId() {
        const id = this._selectedStrokeId();
        return id && this.app.strokeStore?.masters?.has(id) ? id : null;
    }

    _modifier(id) {
        return this.app.strokeStore?.getModifier(id) || null;
    }

    // --- DOM --------------------------------------------------------------

    createPanel() {
        const panel = document.createElement('div');
        panel.className = 'stroke-modifier-panel panel';
        panel.style.cssText = 'display: flex; flex-direction: column; gap: 8px; width: 100%;';

        // Hint (shown when no linked stroke is selected)
        this.hint = document.createElement('div');
        this.hint.className = 'props-hint';
        this.hint.textContent = 'Select a linked stroke to shape it across layers';
        panel.appendChild(this.hint);

        // Content (shown for a linked stroke)
        this.content = document.createElement('div');
        this.content.style.cssText = 'display: none; flex-direction: column; gap: 8px;';
        panel.appendChild(this.content);

        // Enable row
        const enableRow = document.createElement('label');
        enableRow.style.cssText = 'display: flex; align-items: center; gap: 8px; cursor: pointer; font-size: 12px;';
        this.enableBox = document.createElement('input');
        this.enableBox.type = 'checkbox';
        this.enableBox.addEventListener('change', () => this._onToggleEnabled());
        const enableText = document.createElement('span');
        enableText.textContent = 'Enable cross-chair curve';
        enableRow.appendChild(this.enableBox);
        enableRow.appendChild(enableText);
        this.content.appendChild(enableRow);

        // Editor block (hidden until enabled)
        this.editor = document.createElement('div');
        this.editor.style.cssText = 'display: none; flex-direction: column; gap: 6px;';
        this.content.appendChild(this.editor);

        // Shared curve-editor widget bound to the ACTIVE channel of the
        // selected stroke's modifier
        this.curveEditor = new CurveEditor({
            min: this.channel.min,
            max: this.channel.max,
            identity: this.channel.identity,
            sKey: 't',
            vKey: 'v',
            getPoints: () => {
                const mod = this._modifier(this._selectedLinkedId());
                return mod ? mod[this.channel.key] : null;
            },
            setPoints: (pts) => {
                const id = this._selectedLinkedId();
                const mod = cloneModifier(this._modifier(id));
                if (!id || !mod) return;
                mod[this.channel.key] = pts;
                this.app.strokeStore.setModifier(id, mod);
            },
            getTicks: () => {
                const n = this.app.basePlanes?.length || 0;
                return Array.from({ length: n }, (_, i) => (n > 1 ? i / (n - 1) : 0));
            },
            onGestureStart: () => this.app.pushUndoSnapshot(),
            onChangeThrottled: () => this._throttledCommit(),
            onCommit: () => this._commit()
        });

        // Channel selector row + point add/remove
        const chRow = document.createElement('div');
        chRow.style.cssText = 'display: flex; align-items: center; gap: 4px;';
        this.channelButtons = new Map();
        for (const ch of CHANNELS) {
            const btn = document.createElement('button');
            btn.textContent = ch.label;
            btn.title = `Edit the ${ch.label} channel`;
            btn.style.cssText = `
                flex: 1; padding: 4px 0; font-size: 11px; border-radius: 6px;
                border: 1px solid rgba(0,0,0,0.15); background: transparent; cursor: pointer;`;
            btn.addEventListener('click', () => {
                this.channel = ch;
                this._refreshChannelButtons();
                this.curveEditor.setRange(ch);
            });
            this.channelButtons.set(ch.key, btn);
            chRow.appendChild(btn);
        }
        const mkPtBtn = (label, title, fn) => {
            const b = document.createElement('button');
            b.textContent = label;
            b.title = title;
            b.style.cssText = `
                width: 24px; padding: 4px 0; font-size: 12px; border-radius: 6px;
                border: 1px solid rgba(0,0,0,0.15); background: transparent; cursor: pointer;`;
            b.addEventListener('click', fn);
            chRow.appendChild(b);
            return b;
        };
        mkPtBtn('+', 'Add a control point (max 5)', () => this.curveEditor.addPoint());
        mkPtBtn('−', 'Remove a control point (min 2)', () => this.curveEditor.removePoint());
        this.editor.appendChild(chRow);

        this.editor.appendChild(this.curveEditor.element);

        // Axis caption
        const caption = document.createElement('div');
        caption.className = 'props-hint';
        caption.style.padding = '0 2px';
        caption.textContent = 'layer 1 → last layer';
        this.editor.appendChild(caption);

        this.panel = panel;
    }

    _refreshChannelButtons() {
        for (const [key, btn] of this.channelButtons) {
            const active = key === this.channel.key;
            btn.style.background = active ? 'var(--accent-tint, rgba(0,180,220,0.15))' : 'transparent';
            btn.style.borderColor = active ? 'var(--accent, #00b4dc)' : 'rgba(0,0,0,0.15)';
        }
    }

    // --- Store mutations ---------------------------------------------------

    _onToggleEnabled() {
        const id = this._selectedLinkedId();
        if (!id) return;
        this.app.pushUndoSnapshot();
        let mod = cloneModifier(this._modifier(id));
        if (this.enableBox.checked) {
            mod = mod || defaultModifier();
            mod.enabled = true;
        } else if (mod) {
            mod.enabled = false;
        }
        this.app.strokeStore.setModifier(id, mod);
        this._commit();
        this.update();
    }

    /** Resolve + full parametric regeneration through the canonical pipeline */
    _commit() {
        this.app.commitStoreMutation();
    }

    _throttledCommit() {
        const now = performance.now();
        if (now - this._lastRegen >= 150) {
            this._lastRegen = now;
            this._commit();
        }
    }

    // --- Refresh -------------------------------------------------------------

    /**
     * Cheap per-frame check: re-render only when the relevant selection /
     * modifier signature changes (called from the animate loop).
     */
    updateCheap() {
        if (this.curveEditor?.isDragging) return; // don't fight an active drag
        const id = this._selectedLinkedId();
        const mod = id ? this._modifier(id) : null;
        const sig = id
            ? `${id}|${mod ? (mod.enabled ? 'on' : 'off') : 'none'}|${this.app.basePlanes?.length}`
            : 'none';
        if (sig !== this._lastSignature) {
            this._lastSignature = sig;
            this.update();
        }
    }

    /** Full refresh from the current selection + store */
    update() {
        const id = this._selectedLinkedId();
        if (!id) {
            this.hint.style.display = 'block';
            this.content.style.display = 'none';
            return;
        }
        this.hint.style.display = 'none';
        this.content.style.display = 'flex';

        const mod = this._modifier(id);
        const enabled = !!(mod && mod.enabled);
        this.enableBox.checked = enabled;
        this.editor.style.display = enabled ? 'flex' : 'none';
        if (enabled) {
            this._refreshChannelButtons();
            this.curveEditor.setRange(this.channel);
        }
    }
}
